import { useMemo, useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { useExpenses, useUpdateExpense } from '../../hooks/useExpenses.js';
import { useExpenseCancel } from '../../hooks/useExpenseCancel.js';
import { useExpenseCategories } from '../../hooks/useExpenseCategories.js';
import { useCashboxes } from '../../../cashbox/hooks/useCashboxes.js';
import { ExpenseRow } from './ExpenseRow.jsx';
import { ExpenseForm } from './ExpenseForm.jsx';
import { ViewAllToggle, ViewAllNotice } from '../../../../shared/ViewAllToggle.jsx';
import { listParams } from '../../../../shared/viewAll.js';
import { ExpenseDetails } from './ExpenseDetails.jsx';
import { formatIQD } from '../../../../shared/format.js';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function startOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export function ExpensesList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canCreate = hasPermission('finance.expense.create');
  const canCancel = hasPermission('finance.expense.create') && hasPermission('cashbox.transfer');

  const { data: categories = [] } = useExpenseCategories();
  const { data: cashboxes = [] } = useCashboxes();

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [from, setFrom] = useState(startOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [status, setStatus] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [cashboxId, setCashboxId] = useState('');
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [attachmentView, setAttachmentView] = useState(null);
  const [viewing, setViewing] = useState(null);

  const params = useMemo(
    () => ({
      // الخادم يسمّي حقل البحث q — إرسال search كان يُهمل بصمت.
      q: search || undefined,
      categoryId: categoryId || undefined,
      from,
      to,
      status: status || undefined,
      minAmount: minAmount ? Number(minAmount) : undefined,
      maxAmount: maxAmount ? Number(maxAmount) : undefined,
      cashboxId: cashboxId || undefined,
      ...listParams({ all: showAll, page, limit: 20 })
    }),
    [search, categoryId, from, to, status, minAmount, maxAmount, cashboxId, page, showAll]
  );

  const { data, isLoading, error } = useExpenses(params);
  const expenses = data?.expenses || data?.items || data?.data || [];
  // الخادم يعيد { items, total, page, pages, limit } مسطّحاً — لا كائن pagination.
  const pagination = data?.pagination || data?.meta || {
    page: data?.page || page,
    totalPages: data?.pages || 1,
    total: data?.total ?? 0
  };
  const totalAmount = data?.totalAmount ?? expenses.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  const cancel = useExpenseCancel();
  const update = useUpdateExpense();

  async function handleCancel() {
    if (!cancelTarget || !cancelReason.trim()) return;
    await cancel.mutateAsync(
      { id: cancelTarget._id || cancelTarget.id, reason: cancelReason.trim() },
      {
        onSuccess: () => {
          setCancelTarget(null);
          setCancelReason('');
        }
      }
    );
  }

  function attachmentUrl(expense) {
    if (!expense) return null;
    const a = expense.attachment;
    if (typeof a === 'string') return a;
    return a?.url || expense.attachmentUrl || null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[15px] font-bold">المصاريف</div>
          <div className="text-xs text-ink-3 mt-0.5">إجمالي الفترة: <b style={{ color: '#C2570D' }}>{formatIQD(totalAmount)}</b></div>
        </div>
        <div className="flex items-center gap-2">
          <ViewAllToggle
            all={showAll}
            onChange={(v) => { setShowAll(v); setPage(1); }}
            shown={expenses.length}
            total={pagination.total}
          />
          {canCreate ? (
            <button type="button" className="btn btn-primary" onClick={() => { setEditing(null); setFormOpen(true); }}>
              <Icon name="plus" size={14} /> مصروف جديد
            </button>
          ) : null}
        </div>
      </div>

      <Card>
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">بحث</label>
            <input className="input" placeholder="وصف، مورّد..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الفئة</label>
            <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">الكل</option>
              {categories.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">من</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">إلى</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الحالة</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">الكل</option>
              <option value="recorded">مسجل</option>
              <option value="cancelled">ملغي</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">القاصة</label>
            <select className="input" value={cashboxId} onChange={(e) => setCashboxId(e.target.value)}>
              <option value="">الكل</option>
              {cashboxes.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">من مبلغ</label>
            <input type="number" className="input" min="0" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">إلى مبلغ</label>
            <input type="number" className="input" min="0" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
          </div>
        </div>
      </Card>

      {error ? (
        <Card><div className="text-red text-center py-4">تعذّر تحميل المصاريف</div></Card>
      ) : isLoading ? (
        <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>
      ) : expenses.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="finance" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">لا توجد مصاريف في هذه الفترة</div>
            {canCreate ? (
              <button type="button" className="btn btn-primary" onClick={() => setFormOpen(true)}>
                <Icon name="plus" size={14} /> أضف أول مصروف
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">التاريخ</th>
                <th className="p-2.5 text-start">الفئة</th>
                <th className="p-2.5 text-start">المبلغ</th>
                <th className="p-2.5 text-start">الدفع</th>
                <th className="p-2.5 text-start">القاصة</th>
                <th className="p-2.5 text-start">مُنفّذ العملية</th>
                <th className="p-2.5 text-start">الوصف</th>
                <th className="p-2.5 text-start">المرفق</th>
                <th className="p-2.5 text-start">الحالة</th>
                <th className="p-2.5 text-end">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <ExpenseRow
                  key={e._id || e.id}
                  expense={e}
                  canEdit={canCreate}
                  canCancel={canCancel}
                  onView={(x) => setViewing(x)}
                  onEdit={(x) => setEditing(x)}
                  onCancel={(x) => setCancelTarget(x)}
                  onViewAttachment={(x) => setAttachmentView(x)}
                />
              ))}
            </tbody>
          </table>

          {showAll ? (
            <ViewAllNotice all={showAll} shown={expenses.length} total={pagination.total} />
          ) : pagination.totalPages > 1 ? (
            <div className="flex items-center justify-between pt-3 border-t border-line mt-3">
              <div className="text-xs text-ink-3">صفحة {pagination.page || page} من {pagination.totalPages}</div>
              <div className="flex items-center gap-1.5">
                <button type="button" className="btn" style={{ padding: '6px 10px' }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>السابق</button>
                <button type="button" className="btn" style={{ padding: '6px 10px' }} disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage((p) => p + 1)}>التالي</button>
              </div>
            </div>
          ) : null}
        </Card>
      )}

      <ExpenseForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSuccess={() => setPage(1)}
      />

      {viewing ? <ExpenseDetails expense={viewing} onClose={() => setViewing(null)} /> : null}

      {editing ? (
        <EditExpenseModal
          expense={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            await update.mutateAsync(
              { id: editing._id || editing.id, body },
              { onSuccess: () => setEditing(null) }
            );
          }}
          submitting={update.isPending}
        />
      ) : null}

      {cancelTarget ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center"
          style={{ background: 'rgba(0,0,0,.5)' }}
        >
          <div className="card w-[440px] max-w-full" onClick={(e) => e.stopPropagation()}>
            <div className="card-h">
              <h3 className="text-[15px] font-bold flex-1">إلغاء المصروف</h3>
              <button type="button" className="btn btn-ghost" onClick={() => setCancelTarget(null)}>
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="card-b space-y-3">
              <div className="text-[13px] text-ink-2">
                سيتم إلغاء المصروف بمبلغ <b>{formatIQD(cancelTarget.amount || 0)}</b> وعكس أثره على القاصة. اشرح السبب:
              </div>
              <textarea
                className="input"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="سبب الإلغاء..."
              />
            </div>
            <div className="card-h border-t border-b-0 flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setCancelTarget(null)} disabled={cancel.isPending}>
                تراجع
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={cancel.isPending || !cancelReason.trim()}
                onClick={handleCancel}
              >
                {cancel.isPending ? 'جاري...' : 'إلغاء المصروف'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {attachmentView ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center p-4"
          style={{ background: 'rgba(0,0,0,.7)' }}
        >
          <div className="card w-[640px] max-w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="card-h">
              <h3 className="text-[15px] font-bold flex-1">مرفق المصروف</h3>
              <button type="button" className="btn btn-ghost" onClick={() => setAttachmentView(null)}>
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="card-b">
              {(() => {
                const url = attachmentUrl(attachmentView);
                if (!url) return <div className="text-ink-3 text-center py-6">لا يوجد مرفق</div>;
                const isPdf = url.endsWith('.pdf') || (attachmentView.attachment?.mimeType || '').includes('pdf');
                if (isPdf) {
                  return <iframe src={url} title="attachment" className="w-full" style={{ height: 480, border: 0 }} />;
                }
                return <img src={url} alt="attachment" className="max-w-full mx-auto rounded-[8px]" />;
              })()}
            </div>
            <div className="card-h border-t border-b-0 flex justify-end gap-2">
              {attachmentUrl(attachmentView) ? (
                <a className="btn" href={attachmentUrl(attachmentView)} target="_blank" rel="noreferrer">
                  فتح في نافذة جديدة
                </a>
              ) : null}
              <button type="button" className="btn btn-primary" onClick={() => setAttachmentView(null)}>إغلاق</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EditExpenseModal({ expense, onClose, onSubmit, submitting }) {
  const [description, setDescription] = useState(expense.description || '');
  const [supplier, setSupplier] = useState(expense.supplierName || '');
  const [notes, setNotes] = useState(expense.notes || '');

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.5)' }}
    >
      <div className="card w-[460px] max-w-full" onClick={(e) => e.stopPropagation()}>
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">تعديل بيانات المصروف</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="card-b space-y-3">
          <div className="text-[12px] text-ink-3">المبلغ والقاصة لا يمكن تعديلهما — يجب الإلغاء وإعادة الإنشاء.</div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الوصف *</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">المورّد</label>
            <input className="input" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
            <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="card-h border-t border-b-0 flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>إلغاء</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={submitting || !description.trim()}
            onClick={() => onSubmit({ description: description.trim(), supplierName: supplier.trim(), notes: notes.trim() })}
          >
            {submitting ? 'جاري...' : 'حفظ'}
          </button>
        </div>
      </div>
    </div>
  );
}
